import { useEffect, useState } from 'react';
import { scontrini } from '../../api/scontrini';
import type { DettaglioScontrino } from '../../api/scontrini';
import { formattaData, formattaDataOra, formattaImporto, oggiIso } from '../../lib/formato';

type Periodo = 'oggi' | 'settimana' | 'mese';

const PERIODI: { chiave: Periodo; etichetta: string; giorni: number }[] = [
  { chiave: 'oggi', etichetta: 'Oggi', giorni: 0 },
  { chiave: 'settimana', etichetta: '7 giorni', giorni: 6 },
  { chiave: 'mese', etichetta: '30 giorni', giorni: 29 },
];

const STATI: Record<string, string> = {
  DaEmettere: 'non emesso',
  Emesso: 'emesso',
  DaVerificare: 'da verificare',
  Annullato: 'annullato',
};

const giorniFa = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/*  I MIEI SCONTRINI, dal telefono (23/10/2026): «per capire quando uno scontrino e' stato
    emesso». Un elenco a schede, il piu' recente in alto; toccandone uno si vedono righe,
    documento della stampante e a chi e' andata la copia.  */
export function MieiScontrini() {
  const [periodo, setPeriodo] = useState<Periodo>('settimana');
  const [cerca, setCerca] = useState('');
  const [elenco, setElenco] = useState<DettaglioScontrino[] | null>(null);
  const [aperto, setAperto] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    const giorni = PERIODI.find((p) => p.chiave === periodo)?.giorni ?? 0;
    setElenco(null);
    setErrore(null);
    scontrini
      .miei(giorniFa(giorni), oggiIso())
      .then(setElenco)
      .catch((e: Error) => setErrore(e.message));
  }, [periodo]);

  const testo = cerca.trim().toLowerCase();
  const visibili = (elenco ?? []).filter(
    (s) =>
      testo === '' ||
      (s.numero ?? '').toLowerCase().includes(testo) ||
      (s.mailCliente ?? '').toLowerCase().includes(testo) ||
      s.righe.some((r) => r.descrizione.toLowerCase().includes(testo)),
  );

  return (
    <div className="rapportino scontrino">
      <header className="testata-mobile">
        <h1>I miei scontrini</h1>
      </header>

      <div className="viste-pronte" role="group" aria-label="Periodo">
        {PERIODI.map((p) => (
          <button
            key={p.chiave}
            type="button"
            className={periodo === p.chiave ? 'on' : ''}
            aria-pressed={periodo === p.chiave}
            onClick={() => setPeriodo(p.chiave)}
          >
            {p.etichetta}
          </button>
        ))}
      </div>

      <label className="campo-blocco">
        <span>Cerca</span>
        <input
          value={cerca}
          placeholder="numero, articolo o mail del cliente"
          onChange={(e) => setCerca(e.target.value)}
        />
      </label>

      {errore && <p className="errore">{errore}</p>}
      {elenco === null && !errore && <p className="nota">Leggo gli scontrini…</p>}
      {elenco !== null && visibili.length === 0 && <p className="nota">Nessuno scontrino nel periodo.</p>}

      <section>
        {visibili.map((s) => (
          <div key={s.id} className={`scheda-scontrino stato-${s.stato}`}>
            <button
              type="button"
              className="testa"
              aria-expanded={aperto === s.id}
              onClick={() => setAperto(aperto === s.id ? null : s.id)}
            >
              <span className="quando">
                <strong>{s.numero}</strong>
                <small>
                  {s.emessoIl ? `emesso ${formattaDataOra(s.emessoIl)}` : `scritto ${formattaDataOra(s.creatoIl)}`}
                </small>
              </span>
              <span className="quanto">
                <strong>{formattaImporto(s.totale)}</strong>
                <small>
                  {s.pagamento === 'Contanti' ? 'contanti' : 'POS'} · <em>{STATI[s.stato] ?? s.stato}</em>
                </small>
              </span>
            </button>
            {aperto === s.id && (
              <div className="corpo">
                {s.righe.map((r) => (
                  <p key={r.codiceArticolo}>
                    {r.quantita} × {r.descrizione} — {formattaImporto(r.importo)}
                  </p>
                ))}
                <p className="nota">
                  Del {formattaData(s.data)}
                  {s.numeroDocumento && (
                    <> · documento RT n. {s.numeroDocumento}{s.numeroChiusura ? ` (chiusura ${s.numeroChiusura})` : ''}</>
                  )}
                  {s.mailCopia && <> · copia a {s.mailCopia}</>}
                </p>
                {s.ultimoErrore && s.stato !== 'Emesso' && <p className="errore">{s.ultimoErrore}</p>}
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
